/**
 * YH No. (patient PIN) search, shared by every study list (PACS, Worklist, Orders, Dispatch/Consultant, Front Desk, QC Workstation).
 *
 * A YH No. is an exact patient identifier, so when one is entered it overrides every
 * other filter: status, modality, name, date range, etc. are dropped and only that
 * patient's studies come back. Lists name the field differently (`po_pin` vs `pat_pin`),
 * and most carry a centre scope that is an access boundary rather than a search filter;
 * the reset puts that back to the user's default (`scope`) instead of dropping it.
 */

type Filters = Record<string, any>;

export interface YhSearchOptions {
  /** Filter key holding the YH No. Defaults to `po_pin`. */
  pinKey?: string;
  /** The list's default scope (e.g. `{ center: getDefaultCenterFilter() }`), applied with the YH No. */
  scope?: Filters;
}

/** The trimmed YH No. from a filter object, or '' when none is entered. */
export const getYhPin = (filters: Filters | null | undefined, pinKey = 'po_pin'): string => {
  const value = filters?.[pinKey];
  return typeof value === 'string' ? value.trim() : '';
};

/**
 * The filters a search should actually use: just the YH No. (plus `scope`) when one
 * is entered, otherwise the filters unchanged. Callers must also skip the date range
 * whenever `pin` is non-empty.
 */
export const applyYhSearch = (
  filters: Filters | null | undefined,
  { pinKey = 'po_pin', scope }: YhSearchOptions = {}
): { pin: string; filters: Filters } => {
  const pin = getYhPin(filters, pinKey);
  if (!pin) {
    return { pin, filters: filters || {} };
  }
  return { pin, filters: { ...scope, [pinKey]: pin } };
};

export interface RunYhSearchOptions extends YhSearchOptions {
  filters: Filters;
  setFilters: (filters: Filters) => void;
  setDateRange?: (range: null) => void;
  setModalityAllSelected?: (all: boolean) => void;
  /** Called on every search, e.g. to move pagination back to page 1. */
  onBeforeSearch?: () => void;
  /** Runs the list's fetch with the effective filters. */
  search: (filters: Filters) => void;
}

/**
 * Search-button / Enter handler. With a YH No. entered it clears every other filter in
 * the UI (so what's on screen matches what was searched) and searches that patient only;
 * otherwise it searches with the current filters.
 */
export const runYhSearch = ({
  filters,
  setFilters,
  setDateRange,
  setModalityAllSelected,
  onBeforeSearch,
  search,
  ...options
}: RunYhSearchOptions): void => {
  const { pin, filters: effective } = applyYhSearch(filters, options);
  onBeforeSearch?.();
  if (pin) {
    setFilters(effective);
    setDateRange?.(null);
    setModalityAllSelected?.(true);
  }
  search(effective);
};
